import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Proposal, InsertProposal } from "@shared/schema";
import { ProposalForm } from "./ProposalForm";
import { useProposal } from "@/hooks/useProposals";
import { Loader } from "lucide-react";

interface ProposalDialogProps {
  tripId: number;
  proposalId: number | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: InsertProposal) => void;
  isSubmitting: boolean;
}

export function ProposalDialog({
  tripId,
  proposalId,
  isOpen,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: ProposalDialogProps) {
  // Fetch proposal data if editing
  const {
    data: proposal,
    isLoading,
    error,
  } = useProposal(proposalId);

  // Handle the title based on whether creating or editing
  const dialogTitle = proposalId ? "Edit Proposal" : "Create New Proposal";
  
  // Handle form submission
  const handleSubmit = (data: InsertProposal) => {
    onSubmit(data);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>
            {proposalId
              ? "Update the proposal details below"
              : "Fill in the details to create a new proposal"}
          </DialogDescription>
        </DialogHeader>

        {/* Loading state */}
        {proposalId && isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader className="animate-spin h-8 w-8 text-primary" />
            <span className="ml-2">Loading proposal data...</span>
          </div>
        )}

        {/* Error state */}
        {proposalId && error && (
          <div className="bg-destructive/10 text-destructive p-4 rounded-md">
            <p>Error loading proposal. Please try again.</p>
          </div>
        )}

        {/* Form */}
        {(!proposalId || (!isLoading && !error)) && (
          <ProposalForm
            tripId={tripId}
            initialData={proposal}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}